import { NavLink } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  LayoutDashboard,
  Users,
  UserCircle,
  FileText,
  Receipt,
  Bell,
  Mail,
  Bot,
  AlertCircle,
  Settings,
  X,
  LogOut,
  FolderKanban,
  Send,
  CreditCard,
  Calendar,
  Package,
  Ticket,
  Wrench,
  Target,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuth } from '@/contexts/AuthContext'
import { useAssistanceSubmittedCount } from '@/hooks/useAssistance'
import { usePendingRdvCount } from '@/hooks/useAgenda'

interface SidebarProps {
  isOpen: boolean
  onClose: () => void
  isMobile?: boolean
}

const navigation = [
  { name: 'Tableau de bord', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Clients', href: '/clients', icon: UserCircle },
  { name: 'Contacts', href: '/contacts', icon: Users },
  { name: 'Prospection', href: '/prospection', icon: Target },
  { name: 'Projets', href: '/projects', icon: FolderKanban },
  { name: 'Devis', href: '/devis', icon: FileText },
  { name: 'Factures', href: '/invoices', icon: Receipt },
  { name: 'Produits', href: '/products', icon: Package },
  { name: 'Comptabilite', href: '/comptabilite', icon: CreditCard },
  { name: 'Agenda', href: '/agenda', icon: Calendar },
  { name: 'Relances', href: '/reminders', icon: Bell },
  { name: 'Emails', href: '/emails/templates', icon: Mail },
  { name: 'Marketing', href: '/marketing', icon: Send },
  { name: 'Conversations IA', href: '/ai/conversations', icon: Bot },
  { name: 'Interventions', href: '/interventions', icon: AlertCircle },
  { name: 'Tickets', href: '/tickets', icon: Ticket },
  { name: 'Assistance', href: '/assistance', icon: Wrench },
  { name: 'Parametres', href: '/settings', icon: Settings },
]

export default function Sidebar({ isOpen, onClose, isMobile = false }: SidebarProps) {
  const { signOut, user } = useAuth()
  const { data: submittedCount } = useAssistanceSubmittedCount()
  const { data: pendingRdvCount } = usePendingRdvCount()

  const handleSignOut = async () => {
    await signOut()
  }

  const sidebarContent = (
    <div className="flex flex-col h-full bg-white border-r border-beige-200">
      {/* Logo */}
      <div className="flex items-center justify-between h-16 px-6 border-b border-beige-200">
        <h1 className="text-xl font-display font-bold text-gradient">
          CRM Admin
        </h1>
        {isMobile && (
          <button
            onClick={onClose}
            className="text-brown-400 hover:text-brown-600 lg:hidden"
          >
            <X className="w-6 h-6" />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
        {navigation.map((item) => (
          <NavLink
            key={item.name}
            to={item.href}
            onClick={isMobile ? onClose : undefined}
            className={({ isActive }) =>
              cn(
                'flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors',
                isActive
                  ? 'bg-mint-50 text-mint-700'
                  : 'text-brown-600 hover:bg-beige-50 hover:text-brown-900'
              )
            }
          >
            <item.icon className="w-5 h-5 mr-3" />
            {item.name}
            {item.name === 'Agenda' && pendingRdvCount && pendingRdvCount > 0 ? (
              <span className="ml-auto inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold bg-gold-100 text-gold-800 rounded-full">
                {pendingRdvCount}
              </span>
            ) : null}
            {item.name === 'Assistance' && submittedCount && submittedCount > 0 ? (
              <span className="ml-auto inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold bg-purple-100 text-purple-800 rounded-full">
                {submittedCount}
              </span>
            ) : null}
          </NavLink>
        ))}
      </nav>

      {/* User section */}
      <div className="p-4 border-t border-beige-200">
        <div className="flex items-center mb-4">
          <div className="w-10 h-10 rounded-full bg-mint-100 flex items-center justify-center">
            <span className="text-mint-700 font-semibold">
              {user?.email?.charAt(0).toUpperCase()}
            </span>
          </div>
          <div className="ml-3 overflow-hidden">
            <p className="text-sm font-medium text-brown-900 truncate">
              {user?.email}
            </p>
            <p className="text-xs text-brown-500">Administrateur</p>
          </div>
        </div>
        <button
          onClick={handleSignOut}
          className="flex items-center w-full px-4 py-2 text-sm font-medium text-brown-600 rounded-lg hover:bg-beige-50 hover:text-brown-900 transition-colors"
        >
          <LogOut className="w-5 h-5 mr-3" />
          Deconnexion
        </button>
      </div>
    </div>
  )

  if (isMobile) {
    return (
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={onClose}
              className="fixed inset-0 bg-brown-900/50 z-40 lg:hidden"
            />

            {/* Sidebar */}
            <motion.div
              initial={{ x: -280 }}
              animate={{ x: 0 }}
              exit={{ x: -280 }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="fixed inset-y-0 left-0 w-64 z-50 lg:hidden"
            >
              {sidebarContent}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    )
  }

  return sidebarContent
}
